import React, { useState, useEffect } from 'react';
import { Mail, Phone, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import { useLang } from '../context/LanguageContext';
import Logo from './Logo';

const PRIVACY_FALLBACK = { title: 'Datenschutzerklärung', sections: [] };
const IMPRINT_FALLBACK = { title: 'Impressum', sections: [] };
const TERMS_FALLBACK = { title: 'Allgemeine Geschäftsbedingungen (AGB)', sections: [] };

const renderBody = (text) => {
  if (!text) return null;
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n');
    const isList = lines.length > 1 && lines.every((l) => l.trim().startsWith('- '));
    if (isList) {
      return (
        <ul key={bi} className="list-disc list-inside text-[#86868B] space-y-1 ml-4">
          {lines.map((l, li) => (
            <li key={li}>{l.replace(/^-\s*/, '')}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={bi} className="text-[#86868B] leading-relaxed whitespace-pre-line">
        {block}
      </p>
    );
  });
};

export const LegalModal = ({ data, onClose, testId }) => {
  const { t, lang } = useLang();
  return (
  <div
    className="fixed inset-0 bg-[#1D1D1F]/40 backdrop-blur-md z-50 flex items-center justify-center p-6"
    onClick={onClose}
    data-testid={testId}
  >
    <div
      className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-8">
        <div className="flex justify-between items-start mb-6">
          <h1 className="text-3xl font-semibold text-[#1D1D1F]">{data.title}</h1>
          <button onClick={onClose} className="text-[#86868B] hover:text-[#1D1D1F]" data-testid="legal-modal-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {lang !== 'de' && (
          <p className="text-sm text-[#86868B] bg-[#F5F5F7] rounded-xl px-4 py-3 mb-6" data-testid="legal-binding-note">
            {t('footer.legalBinding')}
          </p>
        )}

        <div className="space-y-6 text-[#1D1D1F]">
          {(data.sections || []).map((section, idx) => (
            <section key={idx}>
              {section.heading && (
                <h2 className="text-xl font-semibold mb-3">{section.heading}</h2>
              )}
              {section.subheading && (
                <h3 className="font-semibold mb-2">{section.subheading}</h3>
              )}
              <div className="space-y-3">{renderBody(section.body)}</div>
            </section>
          ))}
        </div>

        <Button
          onClick={onClose}
          className="w-full mt-8 bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12"
        >
          {t('footer.close')}
        </Button>
      </div>
    </div>
  </div>
  );
};

const Footer = ({ showPrivacyExternal, onPrivacyClose }) => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showImprint, setShowImprint] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();
  const [noticePre, noticePost] = t('footer.notice').split('{company}');

  const privacy = useContent('legal_privacy', PRIVACY_FALLBACK);
  const imprint = useContent('legal_imprint', IMPRINT_FALLBACK);
  const terms = useContent('legal_terms', TERMS_FALLBACK);

  // Open privacy modal when external trigger flips on (e.g. from CookieBanner)
  useEffect(() => {
    if (showPrivacyExternal) {
      setShowPrivacy(true);
    }
  }, [showPrivacyExternal]);

  const closePrivacy = () => {
    setShowPrivacy(false);
    if (onPrivacyClose) onPrivacyClose();
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#1D1D1F] py-14 px-6">
      <div className="max-w-[980px] mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div className="md:col-span-2">
            <div className="mb-4" data-testid="footer-logo">
              <Logo dark size="text-xl" markClass="h-4 w-auto" />
            </div>
            <p className="text-[#86868B] text-sm mb-4 max-w-md leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="space-y-2">
              <a
                href="mailto:info@invictaflow.ch"
                className="flex items-center space-x-2 text-[#86868B] hover:text-[#2997FF] transition-colors text-sm"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                <span>info@invictaflow.ch</span>
              </a>
              <a
                href="tel:+41796969899"
                className="flex items-center space-x-2 text-[#86868B] hover:text-[#2997FF] transition-colors text-sm"
              >
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                <span>+41 79 696 98 99</span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm">{t('footer.navigation')}</h3>
            <ul className="space-y-2">
              <li><button onClick={() => scrollToSection('hero')} className="text-[#86868B] hover:text-[#2997FF] transition-colors text-sm">{t('nav.home')}</button></li>
              <li><button onClick={() => scrollToSection('services')} className="text-[#86868B] hover:text-[#2997FF] transition-colors text-sm">{t('nav.services')}</button></li>
              <li><button onClick={() => scrollToSection('projects')} className="text-[#86868B] hover:text-[#2997FF] transition-colors text-sm">{t('nav.references')}</button></li>
              <li><Link to="/plattformen" className="text-[#86868B] hover:text-[#2997FF] transition-colors text-sm" data-testid="footer-platforms-link">{t('nav.platforms')}</Link></li>
              <li><button onClick={() => scrollToSection('pricing')} className="text-[#86868B] hover:text-[#2997FF] transition-colors text-sm">{t('nav.pricing')}</button></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm">{t('footer.servicesTitle')}</h3>
            <ul className="space-y-2 text-[#86868B] text-sm">
              {t('footer.servicesList').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <p className="text-[#86868B] text-xs text-center md:text-left mb-4" data-testid="footer-invictax-notice">
            {noticePre}
            <a
              href="https://www.invictaflow.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2997FF] hover:underline"
            >
              InvictaX Group GmbH
            </a>
            {noticePost}
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-[#86868B] text-xs">
              © {new Date().getFullYear()} InvictaFlow. {t('footer.rights')}
            </p>
            <div className="flex space-x-6">
              <button
                onClick={() => setShowPrivacy(true)}
                className="text-[#86868B] hover:text-[#2997FF] transition-colors text-xs"
                data-testid="footer-privacy-link"
              >
                {t('footer.privacy')}
              </button>
              <button
                onClick={() => setShowTerms(true)}
                className="text-[#86868B] hover:text-[#2997FF] transition-colors text-xs"
                data-testid="footer-terms-link"
              >
                {t('footer.terms')}
              </button>
              <button
                onClick={() => setShowImprint(true)}
                className="text-[#86868B] hover:text-[#2997FF] transition-colors text-xs"
                data-testid="footer-imprint-link"
              >
                {t('footer.imprint')}
              </button>
              <Link
                to="/admin"
                className="text-[#86868B]/60 hover:text-[#2997FF] transition-colors text-xs"
                data-testid="footer-admin-link"
              >
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>

      {showPrivacy && <LegalModal data={privacy} onClose={closePrivacy} testId="privacy-modal" />}
      {showImprint && <LegalModal data={imprint} onClose={() => setShowImprint(false)} testId="imprint-modal" />}
      {showTerms && <LegalModal data={terms} onClose={() => setShowTerms(false)} testId="terms-modal" />}
    </footer>
  );
};

export default Footer;
